import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CheckboxGrid, CheckboxGridItem } from "../../UIPalette/components/CheckboxGrid/CheckboxGrid";
import { communicationSendOptions, communicationTypes, notificationTypes, WisdomModules } from "shared/constants/Modules";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import {
  ClientLookupModel, DivisionLookupModel,
  TargetGroupResponseModel,
  useScheduledPushNotificationFunctionRequest,
  useSendPublishEmailFunctionRequest,
  useSendPushNotificationFunctionRequest
} from "shared/request/myHealthyAdvantageApi";
import { FormSubmitButton } from "../../UIPalette/components/Form/FormSubmitButton";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { getServerErrorMessage } from "shared/utils/serverError";
import { useAdminUserContext } from "core/state/AdminUserContext";
import { RoleKeys } from "core/Constants";
import { Fields } from "./Form/Fields";
import sanitize from "sanitize-html";
import { getFilterDefaultValues } from "./utils/helpers";
import { Hideable } from "shared/UI/Hideable/Hideable";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";
import InputDatePicker from "shared/UI/DatePicker/InputDatePicker/InputDatePicker";

export type Inputs = {
  communicationType: CheckboxGridItem[];
  notificationType: CheckboxGridItem[];
  sendTo: CheckboxGridItem[];
  clientDivision: { client: ClientLookupModel; division: DivisionLookupModel };
  title: string;
  linkUrl: string;
  body: string;
  targetGroup: TargetGroupResponseModel;
  localeId: string;
  emailGroupId: string;
  templateId: string;
  publishDateUtc: Date;
};

export const Communications = () => {
  const today = new Date();
  const { t } = useTranslation();
  const { modalNotify } = useNotifyModalContext();
  const [activeType, setActiveType] = useState<string>();
  const [notificationType, setNotificationType] = useState<string>();
  const [communicationTarget, setCommunicationTarget] = useState<string>();
  const [errorMessage, setErrorMessage] = useState("");

  const { trigger: pushNotificationTrigger } = useSendPushNotificationFunctionRequest();
  const { trigger: scheduledPushNotificationTrigger } = useScheduledPushNotificationFunctionRequest();
  const { trigger: emailTrigger } = useSendPublishEmailFunctionRequest();
  const { isAuthorised } = useAdminUserContext();

  const isTypeAuthorised = useCallback((type: string) => isAuthorised({ role: RoleKeys.HaAdmin, permissions: [`send-${type}s`] }), [isAuthorised]);

  const filterAuthorisedCommunicationTypes = Object.values(communicationTypes).filter((type) => isTypeAuthorised(type));

  const initialFormValues = useMemo(
    () => ({
      communicationType: getFilterDefaultValues(communicationTypes, {
        getSelected: (type) => type === filterAuthorisedCommunicationTypes[0],
        getDisabled: (type) => !isTypeAuthorised(type),
      }),
      notificationType: getFilterDefaultValues(notificationTypes, {
        getSelected: (option) => option === notificationTypes.IMMEDIATE,
      }),
      sendTo: getFilterDefaultValues(communicationSendOptions, {
        getSelected: (option) => option === communicationSendOptions.ALL_USERS,
      }),
      title: "",
      body: "",
      linkUrl: "",
      templateId: "",
      emailGroupId: "",
    }),
    [filterAuthorisedCommunicationTypes, isTypeAuthorised]
  );

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    trigger: validationTrigger,
    formState: { errors, isSubmitSuccessful, isSubmitting },
  } = useForm<Inputs>({
    defaultValues: initialFormValues,
  });

  const onSubmit = async (inputs: Inputs) => {
    const isFormValid = await validationTrigger();
    const handleSanitizeField = (value: string) => sanitize(value, { allowedTags: [], allowedAttributes: {} });

    if (isFormValid) {
      setErrorMessage("");
      const filters = {
        clientId: inputs.clientDivision?.client?.id,
        divisionId: inputs.clientDivision?.division?.id,
        targetGroupId: inputs.targetGroup?.id,
        localeId: inputs.localeId,
      };

      try {
        const formFilters = {
          ...filters,
          clientModule: communicationTarget === communicationSendOptions.ONLY_PERKS_AND_DISCOUNT ? WisdomModules.perksAndDiscounts : undefined,
        };

        if (activeType === communicationTypes.EMAIL) {
          await emailTrigger({
            body: {
              templateId: handleSanitizeField(inputs.templateId),
              emailGroupId: inputs.emailGroupId,
              filters: formFilters,
            },
          });
        }

        if (activeType === communicationTypes.PUSH_NOTIFICATION) {
          const body = {
            title: handleSanitizeField(inputs.title),
            body: handleSanitizeField(inputs.body),
            ...(!!inputs.linkUrl && { linkUrl: handleSanitizeField(inputs.linkUrl) }),
            filters: formFilters,
          };

          if (notificationType === notificationTypes.SCHEDULED) {
            await scheduledPushNotificationTrigger({
              body: {
                ...body,
                publishDateUtc: inputs.publishDateUtc.toISOString(),
              },
            });
          } else {
            await pushNotificationTrigger({
              body,
            });
          }
        }
        modalNotify("success", t("communications.successTitle"), t("communications.successMessage"));
      } catch (error: any) {
        setErrorMessage(getServerErrorMessage(error, t("communications.requestErrorMessage")));
      }
    }
  };

  const handleCommunicationStateChange = (typesList: CheckboxGridItem[], setAction: Dispatch<SetStateAction<string | undefined>>) => {
    const selectedType = typesList.find((type) => type.selected);
    setAction(selectedType?.key);
  };

  const handleChangeCommunication = (state: CheckboxGridItem[], onChange: (state: CheckboxGridItem[]) => void) => {
    onChange(state);
    handleCommunicationStateChange(state, setActiveType);
  };

  const handleChangeSendTo = (state: CheckboxGridItem[], onChange: (state: CheckboxGridItem[]) => void) => {
    onChange(state);
    handleCommunicationStateChange(state, setCommunicationTarget);
  };

  const handleChangeNotificationType = (state: CheckboxGridItem[], onChange: (state: CheckboxGridItem[]) => void) => {
    onChange(state);
    handleCommunicationStateChange(state, setNotificationType);
  };

  const resetCommunicationStates = useCallback(() => {
    handleCommunicationStateChange(getValues("communicationType"), setActiveType);
    handleCommunicationStateChange(getValues("notificationType"), setNotificationType);
    handleCommunicationStateChange(getValues("sendTo"), setCommunicationTarget);
  }, [getValues]);

  useEffect(() => {
    resetCommunicationStates();
  }, [resetCommunicationStates]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ ...initialFormValues, notificationType: getValues("notificationType"), communicationType: getValues("communicationType") });
      resetCommunicationStates();
    }
  }, [getValues, initialFormValues, isSubmitSuccessful, reset, resetCommunicationStates]);

  const submitButtonText = isSubmitting ? t("communications.sendingButton") : t("communications.sendButton");

  return (
    <div>
      <h2 className="mb-4 text-black">{t("communications.title")}</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-6">
        <Controller
          control={control}
          name="communicationType"
          render={({ field: { value, onChange } }) => (
            <CheckboxGrid
              items={value}
              headingText={t("communications.communicationTypeTitle")}
              onChange={(state) => handleChangeCommunication(state, onChange)}
              radioFields={Object.values(communicationTypes)}
            />
          )}
        />
        <Controller
          control={control}
          name="sendTo"
          render={({ field: { value, onChange } }) => (
            <CheckboxGrid
              items={value}
              headingText={t("communications.sendToTitle")}
              onChange={(state) => handleChangeSendTo(state, onChange)}
              radioFields={Object.values(communicationSendOptions)}
            />
          )}
        />
        <Hideable hidden={activeType !== communicationTypes.PUSH_NOTIFICATION}>
          <Controller
            control={control}
            name="notificationType"
            render={({ field: { value, onChange } }) => (
              <CheckboxGrid
                items={value}
                headingText={t("communications.notificationTypeTitle")}
                onChange={(state) => handleChangeNotificationType(state, onChange)}
                radioFields={Object.values(notificationTypes)}
              />
            )}
          />
          <Hideable data-testid="notifications-scheduler" hidden={notificationType !== notificationTypes.SCHEDULED}>
            <Controller
              name="publishDateUtc"
              rules={{
                required: t("communications.schedulerErrorMessage"),
                validate: {
                  minimumTime: (date: Date) => date > today || t("communications.schedulerMinMessage", { minTime: today.toLocaleString() }),
                },
              }}
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <div className="md:w-2/3">
                    <InputDatePicker
                      minDate={today}
                      date={value}
                      state={errors.publishDateUtc ? "error" : undefined}
                      helperText={errors.publishDateUtc?.message}
                      setDate={onChange}
                      placeholder={t("communications.schedulerPlaceholder")}
                      showTimePicker
                    />
                  </div>
                );
              }}
            />
          </Hideable>
        </Hideable>

        <Fields communicationTarget={communicationTarget} activeType={activeType} control={control} errors={errors} />
        <div className="flex pt-10 mt-2">
          <FormSubmitButton text={submitButtonText} type="submit" disabled={isSubmitting} />
        </div>
      </form>
      <ErrorAlert content={errorMessage} />
    </div>
  );
};
